import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FilterDropdown from "../FilterDropdown";
import { filterConfig } from "./filterConfig";
import styles from "./index.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faSearch } from "@fortawesome/free-solid-svg-icons";

const SearchFilters = ({
  makes,
  models,
  modelCodes,
  onMakeSelect,
  onModelSelect,
  ...props
}) => {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({});
  const [searchText, setSearchText] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Split filters
  const visibleFilters = filterConfig.slice(0, 7);
  const extraFilters = filterConfig.slice(7);

  // Load URL filters
  useEffect(() => {
    const params = Object.fromEntries([...searchParams.entries()]);
    setFilters(params);

    if (params.search) {
      setSearchText(params.search);
    }
  }, [searchParams]);

  // Update filter
  const updateFilter = (key, option) => {

    if (key === "make") {
      setFilters(prev => ({
        ...prev,
        make: option.name,
        model: "",
        modelCode: ""
      }));

      onMakeSelect && onMakeSelect(option.id);
      return;
    }

    if (key === "model") {
      setFilters(prev => ({
        ...prev,
        model: option.name,
        modelCode: ""
      }));

      onModelSelect && onModelSelect(option.id);
      return;
    }

    setFilters(prev => ({
      ...prev,
      [key]: option.name,
      ...(key.endsWith("From") && { [key.replace("From", "To")]: "" })
    }));
  };

  // Find cars
  const findCars = () => {

    const filtered = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v)
    );

    const params = new URLSearchParams(filtered).toString();

    navigate(`/vehicles?${params}`);
  };

  // Search text
  const handleSearch = () => {

    const updatedFilters = {
      ...filters,
      search: searchText
    };

    const filtered = Object.fromEntries(
      Object.entries(updatedFilters).filter(([_, v]) => v)
    );

    const params = new URLSearchParams(filtered).toString();

    navigate(`/vehicles?${params}`);
  };

  // Render single filter
  const renderFilter = (filter) => {

    if (filter.type === "range") {
      return (
        <RangeFilter
          key={filter.key}
          label={filter.label}
          options={props[filter.optionsKey] || []}
          valueFrom={filters[`${filter.key}From`]}
          valueTo={filters[`${filter.key}To`]}
          onChangeFrom={(opt) => updateFilter(`${filter.key}From`, opt)}
          onChangeTo={(opt) => updateFilter(`${filter.key}To`, opt)}
        />
      );
    }

    let options = props[filter.optionsKey] || [];

    if (filter.key === "make") options = makes;
    if (filter.key === "model") options = models;
    if (filter.key === "modelCode") options = modelCodes;

    return (
      <FilterDropdown
        key={filter.key}
        options={options}
        placeholder={filter.label}
        value={filters[filter.key]}
        disabled={
          (filter.key === "model" && !filters.make) ||
          (filter.key === "modelCode" && !filters.model)
        }
        onSelect={(opt) => updateFilter(filter.key, opt)}
      />
    );
  };

  return (
    <div className={styles.filterBody}>

      {/* SEARCH BOX */}
      <div className={styles.searchBox}>
        <input
          type="text"
          placeholder="Search vehicles..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className={styles.searchInput}
        />

        <button className={styles.searchBtn} onClick={handleSearch}>
          <FontAwesomeIcon icon={faSearch} />
        </button>
      </div>


      {/* MAIN FILTERS */}
      <div className={styles.filterContainer}>

        {visibleFilters.map(renderFilter)}

        <div>
          <button className={styles.findBtn} onClick={findCars}>
            Find a Car
          </button>

          <button
            className={styles.toggleBtn}
            onClick={() => setShowMoreFilters(!showMoreFilters)}
          >
            {showMoreFilters ? "Filters" : "Filters"}
          </button>
        </div>
      </div>


      {/* EXTRA FILTERS */}
      {showMoreFilters && (
        <div className={styles.extraFilters}>
          {extraFilters.map(renderFilter)}
        </div>
      )}

    </div>
  );
};

export default SearchFilters;



// RANGE FILTER COMPONENT

const RangeFilter = ({
  label,
  options,
  valueFrom,
  valueTo,
  onChangeFrom,
  onChangeTo
}) => {

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredToOptions = valueFrom
    ? options.filter((opt) => Number(opt.name) >= Number(valueFrom))
    : options;

  return (
    <div className={styles.rangeWrapper} ref={dropdownRef}>

      <div
        className={styles.rangeDisplay}
        onClick={() => setOpen(!open)}
      >
        {valueFrom || valueTo
          ? `${valueFrom || "Any"} - ${valueTo || "Any"}`
          : label}

        <FontAwesomeIcon icon={faAngleDown} />
      </div>

      {open && (
        <div className={styles.rangeDropdown}>

          <FilterDropdown
            options={options}
            placeholder="From"
            value={valueFrom}
            onSelect={onChangeFrom}
          />

          <FilterDropdown
            options={filteredToOptions}
            placeholder="To"
            value={valueTo}
            onSelect={onChangeTo}
          />

        </div>
      )}

    </div>
  );
};